import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Admin > Accounts tab. Lists every registered customer with their name,
 * email, account type (Google vs Email), order count, and join date.
 * Includes a search box and a CSV export.
 */
export default function AccountsPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.users;
    return data.users.filter((u) =>
      (u.email || "").toLowerCase().includes(needle) ||
      (u.name || "").toLowerCase().includes(needle)
    );
  }, [data, q]);

  const exportCsv = () => {
    const header = ["email", "name", "account_type", "order_count", "is_admin", "created_at"];
    const lines = [header.join(",")];
    (data?.users || []).forEach((u) => {
      lines.push([
        u.email || "",
        (u.name || "").replace(/,/g, " "),
        u.auth_provider || "",
        u.order_count || 0,
        u.is_admin ? "yes" : "",
        u.created_at || "",
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tiramisu-accounts.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="mt-10 label-xs text-espresso-light">Loading…</div>;

  const badge = (provider) => {
    const label = provider === "google" ? "Google" : provider === "google+email" ? "Google · Email" : "Email";
    const cls = provider === "google" ? "bg-blue-50 text-blue-700" : provider === "google+email" ? "bg-emerald-50 text-emerald-700" : "bg-ivory-100 text-espresso";
    return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${cls}`}>{label}</span>;
  };

  return (
    <div className="mt-8" data-testid="accounts-panel">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h2 className="font-serif text-3xl text-espresso">Accounts</h2>
        <div className="flex items-center gap-3">
          <Input
            placeholder="Search email or name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="accounts-search"
            className="w-64"
          />
          <Button onClick={exportCsv} data-testid="accounts-csv-export" className="bg-espresso hover:bg-espresso-hover text-ivory">
            Export CSV
          </Button>
        </div>
      </div>

      <div className="mt-4 label-xs text-espresso-light">
        <span data-testid="accounts-count">{data.count}</span> registered customer{data.count === 1 ? "" : "s"}
      </div>

      <div className="mt-6 overflow-x-auto border border-line">
        <table className="w-full text-sm">
          <thead className="bg-ivory-100 label-xs text-espresso-light">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">Orders</th>
              <th className="text-left px-4 py-3">Joined</th>
            </tr>
          </thead>
          <tbody data-testid="accounts-table-body">
            {rows.map((u) => (
              <tr key={u.user_id || u.email} className="border-t border-line">
                <td className="px-4 py-3 text-espresso truncate max-w-[280px]">
                  {u.email}
                  {u.is_admin && <span className="ml-2 text-[10px] uppercase tracking-wider text-gold-hover">owner</span>}
                </td>
                <td className="px-4 py-3 text-espresso">{u.name || "—"}</td>
                <td className="px-4 py-3">{badge(u.auth_provider)}</td>
                <td className="px-4 py-3 text-right text-espresso">{u.order_count || 0}</td>
                <td className="px-4 py-3 text-espresso-light">{(u.created_at || "").slice(0, 10) || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-espresso-light">No customers match "{q}"</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
